import { User } from 'interfaces/user'
import moment from 'moment'
import { Card, Col, Button, InputGroup, FormControl } from 'react-bootstrap'
import React, { useState, useEffect } from 'react'
import TextInformationLine from '../text-information-line'
import UserSettingDropDown from './user-setting-dropdown'
import { LINQ_EMAIL_DOMAIN } from 'constants/empty'
import useUpdateUser from 'hooks/use-update-user'
import useUpdateUserMetaData from 'hooks/use-update-user-metadata'
import useUpdateUserResponseLimiting from 'hooks/use-update-user-response-limiting'
import toast from 'react-hot-toast'
import { OrganizationSubscriptionStatus } from 'interfaces/organization-subscription'
import { Organization } from 'interfaces/organization'
import ResponseLimitingDropdown, {
  ResponseLimitingStrategy,
} from './response-limiting-dropdown'
import useGetAppleIdByUserId from 'hooks/use-get-apple-id-by-user-id'
import useUpdateAppleId from 'hooks/use-update-apple-id-details'

const UserProfile = ({
  user,
  setShowPushTokensModal,
  primaryOrg,
}: {
  user: User
  setShowPushTokensModal: React.Dispatch<React.SetStateAction<boolean>>
  primaryOrg: Organization
}) => {
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const [maxPages, setMaxPages] = useState('')
  const [slackId, setSlackId] = useState<string>()
  const [currentResponseLimitingStrategy, setCurrentResponseLimitingStrategy] =
    useState<ResponseLimitingStrategy>('legacy')
  const [contactCardConfigured, setContactCardConfigured] = useState(false)
  const updateUser = useUpdateUser(user?.uuid)
  const updateUserMetaData = useUpdateUserMetaData(user?.uuid, user?.id)
  const updateUserResponseLimiting = useUpdateUserResponseLimiting(
    user?.id,
    user?.uuid
  )
  const { data: appleId } = useGetAppleIdByUserId(user?.id)
  const updateAppleId = useUpdateAppleId()

  useEffect(() => {
    setMaxPages(user?.max_pages?.toString() || '')
  }, [user?.max_pages])

  useEffect(() => {
    if (user?.internal_slack_id) {
      setSlackId(user.internal_slack_id)
    }
  }, [user])

  useEffect(() => {
    if (user?.response_limiting_strategy) {
      setCurrentResponseLimitingStrategy(user.response_limiting_strategy)
    }
  }, [user?.response_limiting_strategy])

  useEffect(() => {
    if (appleId?.contact_card_configured !== undefined) {
      setContactCardConfigured(appleId.contact_card_configured)
    }
  }, [appleId?.contact_card_configured])

  // Check if user has active organization subscription
  const subscriptionStatus =
    primaryOrg?.organization_subscription?.subscription_status
  const hasActiveOrgSubscription =
    subscriptionStatus === OrganizationSubscriptionStatus.Active ||
    subscriptionStatus === OrganizationSubscriptionStatus.Trialing

  const handleUpdateUserToInternalAdmin = ({
    updatedField,
  }: {
    updatedField: boolean
  }) => {
    if (updatedField) {
      if (
        window.confirm(
          `Are you sure you want to make ${user.name} as LINQ INTERNAL ADMIN?`
        )
      ) {
        updateUser.mutate(
          { linq_internal_admin: updatedField },
          {
            onSuccess: () => {
              toast('Update user to Linq internal admin', { icon: `👍` })
            },
          }
        )
      }
    } else {
      updateUser.mutate(
        { linq_internal_admin: updatedField },
        {
          onSuccess: () => {
            toast('Remove user from Linq internal admin', { icon: `👍` })
          },
        }
      )
    }
  }

  const handleUpdateUserToHaveUnlimitedPage = ({
    updatedField,
  }: {
    updatedField: boolean
  }) => {
    updateUser.mutate({ can_have_unlimited_pages: updatedField })
  }

  const handleUpdateNumberOfAllowedPages = (e: React.FormEvent) => {
    e.preventDefault()
    if (maxPages) {
      updateUserMetaData.mutate(
        {
          max_pages_override: parseInt(maxPages),
        },
        {
          onSuccess: () => {
            toast(`Successfully updated user's max pages`, { icon: `👍` })
          },
        }
      )
    }
  }

  const handleUpdateSlackId = (e: React.FormEvent) => {
    e.preventDefault()
    if (slackId) {
      updateUser.mutate(
        {
          internal_slack_id: slackId,
        },
        {
          onSuccess: () => {
            toast(`Successfully updated Slack ID! 🎉`, { icon: `🎈` })
          },
        }
      )
    }
  }

  const handleResponseLimitingChange = (strategy: ResponseLimitingStrategy) => {
    // Optimistically update the local state
    setCurrentResponseLimitingStrategy(strategy)

    updateUserResponseLimiting.mutate(
      {
        response_limiting_strategy: strategy,
      },
      {
        onError: () => {
          // Revert on error
          setCurrentResponseLimitingStrategy(
            user?.response_limiting_strategy || 'legacy'
          )
        },
      }
    )
  }

  const handleUpdateContactCardConfigured = ({
    updatedField,
  }: {
    updatedField: boolean
  }) => {
    if (!appleId?.id) {
      toast.error('No Apple ID found for this user')
      return
    }

    setContactCardConfigured(updatedField)
    updateAppleId.mutate(
      {
        id: appleId.id,
        data: { contact_card_configured: updatedField },
      },
      {
        onSuccess: () => {
          toast.success('Contact card configuration updated successfully')
        },
        onError: () => {
          setContactCardConfigured(!updatedField) // Revert on error
        },
      }
    )
  }

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text)
    setCopiedField(field)
    setTimeout(() => setCopiedField(null), 2000)
  }

  const CopyableField = ({ text, field }: { text: string; field: string }) => (
    <div
      onClick={() => handleCopy(text, field)}
      style={{ cursor: 'pointer', display: 'inline-block' }}
      title={copiedField === field ? 'Copied!' : 'Click to copy'}
    >
      {text}{' '}
      <ion-icon
        name={copiedField === field ? 'checkmark-outline' : 'copy-outline'}
        style={{
          color: copiedField === field ? '#28a745' : '#4da6ff',
          fontSize: '16px',
        }}
      />
    </div>
  )
  return (
    <Col md="12">
      <Card className="mt-4">
        <Card.Header>
          <h4>User Details</h4>
        </Card.Header>
        <Card.Body>
          <TextInformationLine
            label="User ID"
            data={
              user?.id ? (
                <CopyableField text={user.id.toString()} field="userId" />
              ) : (
                'N/A'
              )
            }
          />
          <TextInformationLine label="User UUID" data={user?.uuid} />
          <TextInformationLine
            label="Created At"
            data={moment(user?.created_at).format('LLL')}
          />
          <TextInformationLine label="Name" data={user?.name} />
          <TextInformationLine
            label="Email"
            data={
              user?.email ? (
                <CopyableField text={user.email} field="email" />
              ) : (
                'N/A'
              )
            }
          />
          <TextInformationLine
            label="Phone number"
            data={
              user?.phone_number ? (
                <CopyableField text={user.phone_number} field="phone" />
              ) : (
                'N/A'
              )
            }
          />
          {user?.stripe_user_id && (
            <TextInformationLine
              label="Linq Pay Account"
              dataIsLink
              dataLinkSettings={{
                href: `https://dashboard.stripe.com/connect/accounts/${user?.stripe_user_id}`,
                target: '_blank',
              }}
              data={user?.stripe_user_id}
            />
          )}
          {!hasActiveOrgSubscription && user?.subscription_kind && (
            <TextInformationLine
              label="Linq Subscription Kind"
              data={user.subscription_kind}
            />
          )}
          {user?.subscription_status && (
            <TextInformationLine
              label="Linq Subscription Status"
              data={user.subscription_status}
            />
          )}
          {!hasActiveOrgSubscription && (user?.is_pro_plus || user?.is_pro) && (
            <TextInformationLine
              label="Linq Subscription Tier"
              data={user?.is_pro_plus ? 'Pro Plus ➕' : 'Pro'}
            />
          )}
          {!hasActiveOrgSubscription &&
            user?.subscription_id &&
            (user?.subscription_kind === 'stripe' ? (
              <TextInformationLine
                label="Linq Subscription ID"
                dataIsLink
                dataLinkSettings={{
                  href: `https://dashboard.stripe.com/subscriptions/${user.subscription_id}`,
                  target: '_blank',
                }}
                data={user.subscription_id}
              />
            ) : (
              <TextInformationLine
                label="Linq Subscription ID"
                data={user.subscription_id}
              />
            ))}
          <UserSettingDropDown
            label="Internal Admin: "
            handleUpdate={handleUpdateUserToInternalAdmin}
            value={user?.is_admin}
            setTo="Set To Admin role"
            remove="Remove Admin role"
            isDisabled={!user?.email?.includes(LINQ_EMAIL_DOMAIN)}
          />
          <UserSettingDropDown
            label="Unlimited Pages: "
            handleUpdate={handleUpdateUserToHaveUnlimitedPage}
            value={user?.can_have_unlimited_pages}
            setTo="Enable Unlimited Pages for User"
            remove="Disable Unlimited Pages for User"
          />
          {appleId && (
            <UserSettingDropDown
              label="Contact Card Configured: "
              handleUpdate={handleUpdateContactCardConfigured}
              value={contactCardConfigured}
              setTo="Mark as Configured"
              remove="Mark as Not Configured"
            />
          )}
          <form onSubmit={handleUpdateNumberOfAllowedPages}>
            <label htmlFor="maxPagesInput">Max Pages Override</label>
            <InputGroup className="mb-4 mt-2">
              <FormControl
                id="maxPagesInput"
                autoComplete="off"
                onChange={(e) => setMaxPages(e.target.value)}
                value={maxPages}
                type="number"
                placeholder="Enter User's Max Pages"
                aria-label="User's Max Pages"
              />
              <Button type="submit">Save</Button>
            </InputGroup>
          </form>

          <ResponseLimitingDropdown
            value={currentResponseLimitingStrategy}
            onChange={handleResponseLimitingChange}
            isDisabled={updateUserResponseLimiting.isLoading}
          />

          {user?.is_admin && (
            <form onSubmit={handleUpdateSlackId}>
              <label htmlFor="slackIdInput">Internal Slack ID</label>
              <InputGroup className="mb-4 mt-2">
                <FormControl
                  id="slackIdInput"
                  autoComplete="off"
                  onChange={(e) => setSlackId(e.target.value)}
                  value={slackId}
                  placeholder="Enter User's Slack ID"
                  aria-label="User's Slack ID"
                />
                <Button type="submit">Save</Button>
              </InputGroup>
            </form>
          )}
          <Button
            variant="success"
            onClick={() => setShowPushTokensModal(true)}
            className="mt-3"
          >
            Push Tokens
          </Button>
        </Card.Body>
      </Card>
    </Col>
  )
}
export default UserProfile
